import { useState, useCallback } from "react";
import { Form, Formik } from "formik";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import SearchIcon from "@mui/icons-material/Search";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

import form from "../schemas/forms";
import { initialValues, initialValuesEdit } from "../schemas/initialValues";
import { validationsCreate, validationsEdit } from "../schemas/validations";

import { setMessage, useMaterialUIController } from "context";

import { editCupom, saveCupom } from "services/cupons";
import { getSeachFilterEventos } from "services/evento";

import FormField from "layouts/pages/users/new-user/components/FormField";

import { ICreateCupom, ICupomEvento } from "interfaces/cupom";
import { IEventoResponse } from "interfaces/eventos";
import { colorsCupons } from "helpers/constants";
import { debouce } from "helpers/utils";
import {
  fieldSx,
  modalHeaderSx,
  modalPaperSx,
  saveButtonSx,
} from "../cupons.theme";

type EventoOption = Pick<IEventoResponse, "id" | "titulo">;

interface Props {
  open: boolean;
  data?: ICupomEvento | null;
  onClose: () => void;
  updateListCupom: (type: "edit" | "create", organizador: ICupomEvento) => void;
}

export default function ModalCupom({ open, onClose, data, updateListCupom }: Props) {
  const [, dispatch] = useMaterialUIController();
  const [eventOptions, setEventOptions] = useState<EventoOption[]>([]);
  const [eventLoading, setEventLoading] = useState(false);

  const searchEventos = useCallback(
    debouce(async (search: string) => {
      if (!search || search.length < 2) {
        setEventOptions([]);
        return;
      }
      setEventLoading(true);
      try {
        const resp = await getSeachFilterEventos(1, 15, search);
        setEventOptions(
          (resp.eventos || []).map((e) => ({
            id: e.id,
            titulo: e.titulo,
          })),
        );
      } catch {
        setEventOptions([]);
      } finally {
        setEventLoading(false);
      }
    }, 400),
    [],
  );

  const dataInitialValuesEdit = data && initialValuesEdit(data);
  const qtdCupomOld = data ? data.qtdCupom : 0;

  const { formId, formField } = form;
  const {
    codigoPromocional,
    dataInicio,
    validade,
    qtdCupom,
    tipoPorcentagem,
    valorCupom,
    eventoId,
    colorField,
  } = formField;

  const handleSubmit = async (params: Record<string, unknown>) => {
    const selectedEvent = params.evento as EventoOption | null;
    const eventId = data ? data.evento.id : selectedEvent?.id;

    if (!eventId) {
      setMessage(dispatch, { open: true, text: "Selecione um evento", severity: "error" });
      return;
    }

    const dataCupom: ICreateCupom = {
      codigoPromocional: params.codigoPromocional as string,
      dataInicio: params.dataInicio as string,
      validade: params.validade as string,
      qtdCupom: Number(params.qtdCupom),
      disponivel: data ? (params.disponivel as number) : Number(params.qtdCupom),
      tipoPorcentagem: params.tipoPorcentagem === "Sim",
      valorCupom: Number(params.valorCupom),
      colorField: params.color as string,
      eventId,
    };

    const action = data ? editCupom(dataCupom, data.id, qtdCupomOld) : saveCupom(dataCupom);
    const messageText = data ? "Cupom atualizado com sucesso" : "Cupom criado com sucesso";

    try {
      const resp = await action;
      if (!data && resp.status !== 201) {
        const messageError = resp.data.message || "Ops, algo deu errado";
        setMessage(dispatch, { open: true, text: messageError, severity: "error" });
      } else {
        if (!data) updateListCupom("create", resp.data);
        else updateListCupom("edit", resp.data);
        setMessage(dispatch, { open: true, text: messageText });
        onClose();
      }
    } catch {
      setMessage(dispatch, {
        open: true,
        text: "Erro ao salvar. Tente novamente.",
        severity: "error",
      });
    }
  };

  const isEdit = Boolean(data);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" minWidth={0}>
            <ConfirmationNumberOutlinedIcon sx={{ fontSize: 40, opacity: 0.9 }} />
            <Box minWidth={0}>
              <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
                {isEdit ? "Editar cupom" : "Novo cupom"}
              </Typography>
              <Typography variant="h5" fontWeight="bold" noWrap>
                {isEdit ? data?.codigoPromocional.toUpperCase() : "Cupom promocional"}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.25 }} noWrap>
                {data?.evento?.titulo || "Vincule a um evento e defina o desconto"}
              </Typography>
              {isEdit ? (
                <Box mt={0.75}>
                  <Chip
                    size="small"
                    label={`${data!.qtdCupom - data!.disponivel} / ${data!.qtdCupom} usados`}
                    sx={{
                      bgcolor: "rgba(255, 255, 255, 0.15)",
                      color: "#fff",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              ) : null}
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 0, overflowY: "auto" }}>
        <Formik
          key={data?.id ?? "new"}
          initialValues={data ? dataInitialValuesEdit : initialValues}
          validationSchema={data ? validationsEdit : validationsCreate}
          onSubmit={(values) => {
            void handleSubmit(values);
          }}
        >
          {({ values, errors, touched, handleChange, setFieldValue, isSubmitting }) => (
            <Form id={formId}>
              <MDBox p={3}>
                <Stack direction="row" spacing={1} alignItems="center" mb={2.5}>
                  <ConfirmationNumberOutlinedIcon color="primary" sx={{ color: "#F96030" }} />
                  <MDTypography variant="button" fontWeight="bold">
                    Dados do cupom
                  </MDTypography>
                </Stack>

                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormField
                      type={codigoPromocional.type}
                      label={codigoPromocional.label}
                      name={codigoPromocional.name}
                      required
                      variant="outlined"
                      value={values.codigoPromocional}
                      onChange={handleChange}
                      onBlur={() =>
                        setFieldValue(
                          "codigoPromocional",
                          String(values.codigoPromocional)
                            .toUpperCase()
                            .replace(/\s+/g, "")
                            .trim(),
                        )
                      }
                      error={!!(errors.codigoPromocional && touched.codigoPromocional)}
                      success={!!values.codigoPromocional && !errors.codigoPromocional}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormField
                      type={dataInicio.type}
                      label={dataInicio.label}
                      name={dataInicio.name}
                      required
                      variant="outlined"
                      value={values.dataInicio}
                      onChange={handleChange}
                      error={!!(errors.dataInicio && touched.dataInicio)}
                      InputLabelProps={{ shrink: true }}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormField
                      type={validade.type}
                      label={validade.label}
                      name={validade.name}
                      required
                      variant="outlined"
                      value={values.validade}
                      onChange={handleChange}
                      error={!!(errors.validade && touched.validade)}
                      InputLabelProps={{ shrink: true }}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormField
                      type={qtdCupom.type}
                      label={qtdCupom.label}
                      name={qtdCupom.name}
                      required
                      variant="outlined"
                      value={values.qtdCupom}
                      onChange={handleChange}
                      error={!!(errors.qtdCupom && touched.qtdCupom)}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormField
                      type={valorCupom.type}
                      label={valorCupom.label}
                      name={valorCupom.name}
                      required
                      variant="outlined"
                      value={values.valorCupom}
                      onChange={handleChange}
                      error={!!(errors.valorCupom && touched.valorCupom)}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      value={values.tipoPorcentagem || null}
                      options={["Sim", "Não"]}
                      onChange={(_, newValue) =>
                        setFieldValue(tipoPorcentagem.name, newValue ?? "")
                      }
                      getOptionLabel={(opt) => String(opt)}
                      isOptionEqualToValue={(opt, val) => opt === val}
                      renderInput={(params) => (
                        <FormField
                          {...params}
                          variant="outlined"
                          name={tipoPorcentagem.name}
                          required
                          label={tipoPorcentagem.label}
                          InputLabelProps={{ shrink: true }}
                          sx={fieldSx}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      value={values.color || null}
                      options={colorsCupons}
                      onChange={(_, newValue) => setFieldValue(colorField.name, newValue ?? "")}
                      getOptionLabel={(opt) => String(opt)}
                      isOptionEqualToValue={(opt, val) => opt === val}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: "50%",
                              bgcolor: String(option),
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          />
                          {String(option)}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <FormField
                          {...params}
                          variant="outlined"
                          name={colorField.name}
                          required
                          label={colorField.label}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: values.color ? (
                              <InputAdornment position="start">
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "50%",
                                    bgcolor: String(values.color),
                                    border: "1px solid",
                                    borderColor: "divider",
                                  }}
                                />
                              </InputAdornment>
                            ) : null,
                          }}
                          sx={fieldSx}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete<EventoOption>
                      value={
                        values.evento &&
                        typeof values.evento === "object" &&
                        "id" in values.evento
                          ? (values.evento as EventoOption)
                          : null
                      }
                      options={
                        data
                          ? [
                              { id: data.evento.id, titulo: data.evento.titulo },
                              ...eventOptions.filter((e) => e.id !== data.evento.id),
                            ]
                          : eventOptions
                      }
                      loading={eventLoading}
                      disabled={!!data}
                      onInputChange={(_, value) => searchEventos(value)}
                      onChange={(_, newValue) => setFieldValue(eventoId.name, newValue)}
                      getOptionLabel={(opt) => (typeof opt === "object" && opt?.titulo) || ""}
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      noOptionsText="Digite para buscar um evento (mín. 2 caracteres)"
                      loadingText="Buscando eventos..."
                      renderInput={(params) => (
                        <MDBox>
                          <MDInput
                            {...params}
                            variant="outlined"
                            required
                            label={eventoId.label}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <>
                                  {eventLoading ? (
                                    <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            error={!!(errors.evento && touched.evento)}
                            sx={fieldSx}
                          />
                          {errors.evento && touched.evento && (
                            <MDTypography
                              variant="caption"
                              color="error"
                              sx={{ mt: 0.5, display: "block" }}
                            >
                              {String(errors.evento)}
                            </MDTypography>
                          )}
                        </MDBox>
                      )}
                    />
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox
                px={3}
                py={2}
                display="flex"
                gap={1}
                justifyContent="flex-end"
                borderTop="1px solid"
                borderColor="grey.200"
              >
                <MDButton
                  type="button"
                  variant="outlined"
                  color="dark"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancelar
                </MDButton>
                <MDButton
                  type="submit"
                  variant="gradient"
                  color="dark"
                  disabled={isSubmitting}
                  sx={saveButtonSx}
                >
                  {isSubmitting
                    ? "Salvando…"
                    : isEdit
                      ? "Salvar alterações"
                      : "Criar cupom"}
                </MDButton>
              </MDBox>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
